import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Card,
  CardHeader,
  CardSection,
  CondensedSmall,
  DateUtil,
  Flashy,
  Form,
  Grid,
  GridColumn,
  Gutter,
  Layout,
  LayoutItem,
  Paragraph,
  ParagraphHeader,
  SectionHeader,
  SmallHeader,
  Toggle,
  useApi,
} from '@axiom/ui';
import { isLpUser } from '@axiom/utils';
import { CandidateSchema } from '@axiom/validation';

import { CandidateApi } from '../../api/candidate-api';
import { UserApi } from '../../api/user-api';
import { PreferencesNavigation } from '../PreferencesNavigation/PreferencesNavigation';
import { CandidateUtil } from '../../utils/candidate-util';
import { PreferencesUtil } from '../../utils/preferences-util';
import { CandidateProfileIssuesApi } from '../../api/candidate-profile-issues-api';
import { FeatureVisibilityUtil } from '../../utils/feature-visibility-util';

import { PreferencesBanner } from './PreferencesBanner';

const EditProgramsPreferencesSchema = CandidateSchema.partial().pick({
  openToSecondment: true,
  openToLawFirms: true,
  openToVariableHourlyProjects: true,
  openToLargeProjects: true,
  openToPermanentPlacement: true,
});

export const PreferencesPrograms = () => {
  const navigate = useNavigate();

  const [{ data: user }] = useApi(UserApi.readUser());
  const { candidateId: urlCandidateId } = useParams();

  const candidateId = CandidateUtil.reconcileUserCandidateId(
    user,
    urlCandidateId
  );

  const [{ data: candidate }] = useApi(CandidateApi.readCandidate(candidateId));
  const { programsPreferencesReviewedAt } = candidate;

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    if (
      FeatureVisibilityUtil.shouldSeeLimitedFeatureSetBasedOnProfileStatus(
        candidate
      )
    ) {
      void navigate('../communications');
    } else if (isLpUser(user)) {
      timeout = setTimeout(() => {
        void CandidateApi.updateCandidate(candidateId, {
          programsPreferencesReviewedAt: DateUtil.formatAsTimestamp(new Date()),
        }).then(async () =>
          Promise.all([
            CandidateApi.refreshCandidate(candidateId),
            CandidateProfileIssuesApi.refreshCandidateProfileIssues(
              candidateId
            ),
          ])
        );
      }, 500);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, []);

  return (
    <Grid name="PROGRAMS_PREFERENCES">
      <GridColumn largeScreenWidth={4} smallScreenWidth={12}>
        <PreferencesNavigation />
        <Gutter only="smallScreen" bottom="16px" />
      </GridColumn>
      <GridColumn largeScreenWidth={8} smallScreenWidth={12}>
        {PreferencesUtil.shouldPreferenceNeedReview(
          programsPreferencesReviewedAt
        ) && <PreferencesBanner />}
        <Form
          name="PREFERENCES_PROGRAMS_FORM"
          schema={EditProgramsPreferencesSchema}
          onSubmit={async formData => {
            await CandidateApi.updateCandidate(candidateId, {
              ...CandidateSchema.partial().parse(formData),
              programsPreferencesReviewedAt: DateUtil.formatAsTimestamp(
                new Date()
              ),
            });
            return Promise.all([
              CandidateApi.refreshCandidate(candidateId),
              CandidateProfileIssuesApi.refreshCandidateProfileIssues(
                candidateId
              ),
            ]);
          }}
          initialValues={{
            openToSecondment: !!candidate.openToSecondment,
            openToLawFirms: !!candidate.openToLawFirms,
            openToVariableHourlyProjects:
              !!candidate.openToVariableHourlyProjects,
            openToLargeProjects: !!candidate.openToLargeProjects,
            openToPermanentPlacement: !!candidate.openToPermanentPlacement,
          }}
          submitOnChange
        >
          {() => (
            <Card>
              <CardHeader>
                <SmallHeader>Programs</SmallHeader>
              </CardHeader>
              <CardSection divider>
                <SectionHeader>
                  Select which Axiom Programs you are open to participate in, if
                  they are available in your area.
                </SectionHeader>
              </CardSection>
              <CardSection divider>
                <Layout
                  stackableOn="mobile"
                  horizontalGutter="24px"
                  position="stretch space-between"
                >
                  <LayoutItem>
                    <ParagraphHeader>
                      <Flashy bold>
                        I’m open to being seconded to a client
                      </Flashy>
                    </ParagraphHeader>
                    <Gutter bottom="16px" />
                    <Paragraph>
                      <Flashy bold>Secondments:</Flashy> Clients engage Axiom
                      legal talent for a fixed number of hours or days each
                      week, either full-time or part-time for a period of time.
                    </Paragraph>
                    <Gutter bottom="16px" only="mobile" />
                  </LayoutItem>
                  <LayoutItem>
                    <Toggle name="openToSecondment" />
                  </LayoutItem>
                </Layout>
              </CardSection>
              <CardSection divider>
                <Layout
                  stackableOn="mobile"
                  horizontalGutter="24px"
                  position="stretch space-between"
                >
                  <LayoutItem>
                    <ParagraphHeader>
                      <Flashy bold>
                        I'm open to working for a law firm client
                      </Flashy>
                    </ParagraphHeader>
                    <Gutter bottom="16px" />
                    <Paragraph>
                      <Flashy bold>Law Firms:</Flashy> Some law firm clients
                      engage Axiom legal talent to access expertise and
                      resources they need to serve their clients. You would
                      still work for Axiom, but would be engaged by a law firm
                      client.
                    </Paragraph>
                    <Gutter bottom="16px" only="mobile" />
                  </LayoutItem>
                  <LayoutItem>
                    <Toggle name="openToLawFirms" />
                  </LayoutItem>
                </Layout>
              </CardSection>
              <CardSection divider>
                <Layout
                  stackableOn="mobile"
                  horizontalGutter="24px"
                  position="stretch space-between"
                >
                  <LayoutItem>
                    <ParagraphHeader>
                      <Flashy bold>
                        I’m open to working on variable hourly projects
                      </Flashy>
                    </ParagraphHeader>
                    <Gutter bottom="16px" />
                    <Paragraph>
                      <Flashy bold>Variable hourly:</Flashy> Clients engage
                      Axiom legal talent on a variable hourly basis sometimes
                      requiring on-demand availability.
                    </Paragraph>
                    <Gutter bottom="16px" only="mobile" />
                  </LayoutItem>
                  <LayoutItem>
                    <Toggle name="openToVariableHourlyProjects" />
                  </LayoutItem>
                </Layout>
              </CardSection>
              <CardSection divider>
                <Layout
                  stackableOn="mobile"
                  horizontalGutter="24px"
                  position="stretch space-between"
                >
                  <LayoutItem>
                    <ParagraphHeader>
                      <Flashy bold>
                        I’m open to working with a team of Axiom legal talent
                      </Flashy>
                    </ParagraphHeader>
                    <Gutter bottom="16px" />
                    <Paragraph>
                      <Flashy bold>Projects:</Flashy> Clients engage a team of
                      Axiom legal talent to work together on initiatives that
                      vary in size and complexity. Projects require an ability
                      to work well in structured teams to meet deadlines.
                    </Paragraph>
                    <Gutter bottom="16px" only="mobile" />
                  </LayoutItem>
                  <LayoutItem>
                    <Toggle name="openToLargeProjects" />
                  </LayoutItem>
                </Layout>
              </CardSection>
              <CardSection divider>
                <Layout
                  stackableOn="mobile"
                  horizontalGutter="24px"
                  position="stretch space-between"
                >
                  <LayoutItem>
                    <ParagraphHeader>
                      <Flashy bold>
                        I’m open to being placed permanently with a client
                      </Flashy>
                    </ParagraphHeader>
                    <Gutter bottom="16px" />
                    <Paragraph>
                      <Flashy bold>Permanent placement:</Flashy> Clients
                      contract with Axiom to fill full-time positions in their
                      legal departments or for a secondment with the intention
                      of hiring permanently after a period of time.
                    </Paragraph>
                    <Gutter bottom="16px" only="mobile" />
                  </LayoutItem>
                  <LayoutItem>
                    <Toggle name="openToPermanentPlacement" />
                  </LayoutItem>
                </Layout>
              </CardSection>
            </Card>
          )}
        </Form>
        {!!programsPreferencesReviewedAt && (
          <Gutter top="10px">
            <CondensedSmall>
              <Flashy
                italic
                color="textSubtle"
                name="PROGRAMS_PREFERENCES_REVIEWED_AT"
              >
                Last reviewed:{' '}
                {DateUtil.displayDateFullYearShortMonthDayName(
                  programsPreferencesReviewedAt
                )}
              </Flashy>
            </CondensedSmall>
          </Gutter>
        )}
      </GridColumn>
    </Grid>
  );
};
